import {
  faArchive,
  faCreditCardBlank,
  faDashboard,
  faMoon,
  faQuestionCircle,
  faSunBright,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Typography } from 'src/components/Typography';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { signOutUser } from 'src/models/user/user';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { isDreamcoatActive } from 'src/shared/dreamcoat';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import toast from 'src/utils/toast';

interface UserDropdownProps {
  showV2Link?: boolean;
}

export const UserDropdown: React.FC<UserDropdownProps> = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.labels' });
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'common.messages',
  });
  const { darkmodeDisabled, isDarkmode, setDarkmode, user, session } =
    useSession();

  const [open, setOpen] = useState(false);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const onClickSetDarkMode = () => {
    setDarkmode(true);
  };
  const setLightMode = () => {
    setDarkmode(false);
  };

  const dreamcoatFrame = isDreamcoatActive(session, user);

  const handleSignOut = async () => {
    if (user?.managerLoginActive) {
      window.location.replace(externalLinks.managerLogout);
      return;
    }

    try {
      await signOutUser();
      window.location.replace(externalLinks.login);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
      toast.error(
        tMessages('An error occurred while signing out. Please try again.')
      );
      // Optionally, you can show an error message to the user here
    }
  };

  const useDarkMode = isDarkmode();
  const showDarkModeControls =
    !darkmodeDisabled && hasFeatureFlag(session, FeatureFlag.DarkMode);

  return (
    <DropdownMenu
      open={open}
      setOpen={setOpen}
      drawerOnMobile
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      trigger={
        dreamcoatFrame && !smDown ? (
          <Button
            variant="tertiary"
            sx={{
              '&&&&': {
                borderRadius: '50svh',
                px: '4px',
              },
            }}
          >
            <Avatar
              src={user?.profileImage}
              size="small"
              initials="DM"
              sx={{ mr: 0.5 }}
            >
              {getUserInitials(user)}
            </Avatar>
            {!smDown && (
              <Icon size="small" color="neutral" icon={faCaretDown} />
            )}
          </Button>
        ) : (
          <IconButton onClick={() => setOpen(!open)}>
            <Avatar src={user.profileImage} size="small">
              {getUserInitials(user)}
            </Avatar>
          </IconButton>
        )
      }
    >
      <ListItem
        primary={getUserDisplayName(user)}
        secondary={user.email}
        avatar={
          <Avatar src={user.profileImage} size="small">
            {getUserInitials(user)}
          </Avatar>
        }
        divider
      />
      {!showDarkModeControls ? null : (
        <Box>
          <Box px={2} pt={1} pb={2}>
            <Typography variant="overline">{t('Color Scheme')}</Typography>
            <Box pt={1}>
              <Chip
                onClick={onClickSetDarkMode}
                icon={<Icon icon={faMoon} />}
                label={t('Dark')}
                active={useDarkMode}
                sx={{ pl: 1, mr: 1 }}
              />
              <Chip
                onClick={setLightMode}
                icon={<Icon icon={faSunBright} />}
                label={t('Light')}
                active={!useDarkMode}
                sx={{ pl: 1, mr: 1 }}
              />
            </Box>
          </Box>
          <Divider />
        </Box>
      )}
      <Box px={1}>
        <PermissionCheck can="viewStaffUserMenu">
          <ListItem
            component={Link}
            href={
              hasFeatureFlag(session, FeatureFlag.NewDashboard) ||
              user?.newDashboard
                ? '/ex4/dashboard'
                : externalLinks.trainerDashboard
            }
            sx={{ my: 1 }}
            icon={faArchive}
            primary={t('Staff Member Dashboard')}
            clickable
            rounded
          />
        </PermissionCheck>
        <ListItem
          component={RouteLink}
          to={'/me/notifications'}
          icon={faDashboard}
          primary={t('Notification Settings')}
          sx={{ my: 1 }}
          clickable
          rounded
        />
        <PermissionCheck can="viewBillingInfo">
          <ListItem
            component={RouteLink}
            to={'/me/account'}
            icon={faCreditCardBlank}
            primary={t('Billing Information')}
            sx={{ my: 1 }}
            clickable
            rounded
          />
        </PermissionCheck>
        <PermissionCheck can="viewStaffUserMenu">
          <ListItem
            component={Link}
            href={externalLinks.support}
            onClick={() => setOpen(false)}
            icon={faQuestionCircle}
            primary={t('Support')}
            sx={{ my: 1 }}
            clickable
            rounded
          />
        </PermissionCheck>
        <ListItem
          component={RouteLink}
          to={'/me/profile'}
          icon={faUser}
          primary={t('Profile')}
          sx={{ my: 1 }}
          clickable
          rounded
        />
      </Box>
      <Divider />
      <Box p={1}>
        <Button size="small" component={Link} onClick={handleSignOut} fullWidth>
          {t('Sign Out')}
        </Button>
      </Box>
    </DropdownMenu>
  );
};
